import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import DashboardLayout from "@/components/layout/DashboardLayout";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import StatusBadge from "@/components/StatusBadge";
import { fileUrl } from "@/lib/files";
import { FileText, Image as ImageIcon } from "lucide-react";

const CHECKLIST_ITEMS = [
  { key: "hs_code", label: "HS code confirmed" },
  { key: "origin_verified", label: "Origin verified" },
  { key: "certifications", label: "Certifications valid" },
  { key: "images", label: "Product images acceptable" },
  { key: "docs", label: "Compliance documents received" },
  { key: "labelling", label: "EU labelling / language OK" },
  { key: "supplier_diligence", label: "Supplier due-diligence complete" },
];

export default function AdminProductReview() {
  const { pid } = useParams();
  const nav = useNavigate();
  const [p, setP] = useState(null);
  const [checklist, setChecklist] = useState({});
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/products/${pid}`).then((r) => {
      setP(r.data);
      setChecklist(r.data.checklist || {});
      setNotes(r.data.admin_notes || "");
    });
  }, [pid]);

  const decide = async (decision) => {
    setBusy(true);
    try {
      await api.post(`/admin/products/${pid}/decision`, { decision, checklist, notes });
      toast.success(`Product ${decision}`);
      nav("/admin/products");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const saveChecklist = async () => {
    try { await api.patch(`/admin/products/${pid}/checklist`, { checklist }); toast.success("Checklist saved"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  if (!p) return <DashboardLayout title="Review"><div>Loading…</div></DashboardLayout>;

  return (
    <DashboardLayout title={`Review: ${p.name}`}>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <Card>
            <CardHeader><CardTitle className="font-heading tracking-tight flex items-center justify-between">Product overview <StatusBadge status={p.status} /></CardTitle></CardHeader>
            <CardContent>
              <p className="text-muted-foreground whitespace-pre-wrap leading-relaxed">{p.description}</p>
              <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div><div className="overline text-muted-foreground">Category</div><div className="mt-1 text-secondary">{p.category}</div></div>
                <div><div className="overline text-muted-foreground">Origin</div><div className="mt-1 text-secondary">{p.origin_country}</div></div>
                <div><div className="overline text-muted-foreground">HS code</div><div className="mt-1 text-secondary">{p.hs_code || "—"}</div></div>
                <div><div className="overline text-muted-foreground">MOQ</div><div className="mt-1 text-secondary">{p.moq} {p.unit}</div></div>
              </div>
              {p.supplier && (
                <div className="mt-6 p-4 bg-muted/40 rounded-md">
                  <div className="overline text-muted-foreground">Supplier</div>
                  <div className="mt-1 font-medium text-secondary">{p.supplier.company_name || p.supplier.name}</div>
                  <div className="text-xs text-muted-foreground">{p.supplier.country}</div>
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle className="font-heading tracking-tight flex items-center gap-2"><ImageIcon className="w-4 h-4"/> Images ({p.image_ids?.length || 0})</CardTitle></CardHeader>
            <CardContent>
              {p.image_ids?.length ? (
                <div className="grid grid-cols-3 gap-3">{p.image_ids.map((id) => <a key={id} href={fileUrl(id)} target="_blank" rel="noreferrer"><img src={fileUrl(id)} alt="" className="w-full h-32 object-cover rounded-md" data-testid={`review-img-${id}`}/></a>)}</div>
              ) : <div className="text-sm text-muted-foreground">No images</div>}
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle className="font-heading tracking-tight flex items-center gap-2"><FileText className="w-4 h-4"/> Documents ({p.document_ids?.length || 0})</CardTitle></CardHeader>
            <CardContent>
              {p.document_ids?.length ? (
                <ul className="space-y-2 text-sm">{p.document_ids.map((id) => <li key={id}><a href={fileUrl(id)} target="_blank" rel="noreferrer" className="text-primary hover:underline break-all" data-testid={`review-doc-${id}`}>Document {id}</a></li>)}</ul>
              ) : <div className="text-sm text-muted-foreground">No documents</div>}
            </CardContent>
          </Card>
        </div>

        <div className="space-y-6">
          <Card className="card-lift">
            <CardHeader><CardTitle className="font-heading tracking-tight">Compliance checklist</CardTitle></CardHeader>
            <CardContent className="space-y-3" data-testid="checklist">
              {CHECKLIST_ITEMS.map((it) => (
                <label key={it.key} className="flex items-center gap-3 cursor-pointer">
                  <Checkbox checked={!!checklist[it.key]} onCheckedChange={(v) => setChecklist({ ...checklist, [it.key]: !!v })} data-testid={`chk-${it.key}`} />
                  <span className="text-sm">{it.label}</span>
                </label>
              ))}
              <Button size="sm" variant="outline" onClick={saveChecklist} className="w-full" data-testid="save-checklist">Save checklist</Button>
            </CardContent>
          </Card>

          <Card className="card-lift">
            <CardHeader><CardTitle className="font-heading tracking-tight">Decision</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <Label>Notes to supplier</Label>
              <Textarea data-testid="admin-notes" rows={4} value={notes} onChange={(e)=>setNotes(e.target.value)} placeholder="Explain approval or what's missing…"/>
              <Button data-testid="approve-btn" onClick={() => decide("approved")} disabled={busy} className="w-full">Approve & publish</Button>
              <Button data-testid="reject-btn" onClick={() => decide("rejected")} disabled={busy} variant="outline" className="w-full">Reject</Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </DashboardLayout>
  );
}
